/**
 * Reusable error display component.
 * Shows main error message and validation details in a user-friendly format.
 */

import React from 'react';
import { AlertCircle, X } from 'lucide-react';

interface ValidationError {
  field: string;
  message: string;
}

interface ErrorDisplayProps {
  /** Main error message */
  message: string | null;
  /** Validation error details from backend */
  validationErrors?: ValidationError[];
  /** Whether to show a dismiss button */
  dismissible?: boolean;
  /** Callback when error is dismissed */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Variant style */
  variant?: 'error' | 'warning' | 'info';
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  message,
  validationErrors = [],
  dismissible = false,
  onDismiss,
  className = '',
  variant = 'error',
}) => {
  if (!message && validationErrors.length === 0) {
    return null;
  }

  const variantStyles = {
    error: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
    warning: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
    info: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  };

  const iconColors = {
    error: 'text-red-500 dark:text-red-400',
    warning: 'text-yellow-500 dark:text-yellow-400',
    info: 'text-blue-500 dark:text-blue-400',
  };

  return (
    <div
      className={`p-4 rounded-lg border ${variantStyles[variant]} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColors[variant]}`} />
        
        <div className="flex-1 min-w-0">
          {/* Main error message */}
          {message && (
            <p className="text-sm font-medium">{message}</p>
          )}
          
          {/* Validation error details */}
          {validationErrors.length > 0 && (
            <div className="mt-2 space-y-1">
              {validationErrors.map((error, index) => (
                <p key={index} className="text-sm">
                  <span className="font-medium">{error.field}:</span> {error.message}
                </p>
              ))}
            </div>
          )}
        </div>
        
        {/* Dismiss button */}
        {dismissible && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex-shrink-0 p-1 rounded-md hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Compact error display for inline form errors.
 * Shows only the message without validation details.
 */
export const InlineError: React.FC<{
  message: string | null | undefined;
  className?: string;
}> = ({ message, className = '' }) => {
  if (!message) {
    return null;
  }

  return (
    <p
      className={`text-sm text-red-500 dark:text-red-400 mt-1 ${className}`}
      role="alert"
    >
      {message}
    </p>
  );
};

export default ErrorDisplay;